/* @flow */

import React from 'react'
import { Map } from 'immutable'
import NavigationButtons from 'atpjs/components/navigation_buttons'
import Error from 'atpjs/components/form/error'
import i18n from 'atpjs/helpers/i18n'
import { validationHasError } from 'atpjs/validation/validations'
import { equals, validDate } from 'atpjs/validation/validators'
import Step from 'tve/views/step'
import Application from 'tve/components/application'
import Navigation from 'tve/components/navigation'
import AppointmentGrid from 'tve/components/appointment_grid'
import AppointmentGridHeader from 'tve/components/appointment_grid_header'
import AppointmentGridFooter from 'tve/components/appointment_grid_footer'
import AppointmentSummary from 'tve/components/appointment_summary'
import { loadSlots } from 'tve/api/atp'
import { topicsBySubject } from 'tve/helpers/logic'
import { computePhone } from 'tve/helpers/phone'
import { getWeeksSlotsHistory } from 'tve/helpers/calendar'

import type { PrefixType } from 'atpjs/helpers/i18n'
import type { StepPropsType, StepStateType } from 'atpjs/wizard/step'

const t = i18n.prefix(['appointment'])

class Appointment<Props: StepPropsType, State: StepStateType> extends Step<Props, State> {
  sendData: boolean = false
  weeksSlotsHistory = []

  validations(_trans: PrefixType) {
    return {
      event: {
        available: equals(true),
        end: validDate(),
        start: validDate()
      }
    }
  }

  loadSlots = (date: Date) => {
    const advisor = this.getData(['advisor']) || Map({})
    const branch = this.getData(['branch']) || Map({})
    const data = this.getHandler()

    const filter = {
      supportedMedia: data.get('medium')
    }

    const topicIds = topicsBySubject(data, filter).map((topic) => topic.get('id'))

    const slots = loadSlots(
      date,
      this.getData(['locale']),
      this.getData(['medium']),
      topicIds,
      branch.get('code'),
      advisor.get('email'),
      this.getData(['appointment', 'street']),
      this.getData(['appointment', 'zip']),
      this.getData(['appointment', 'city'])
    )

    this.weeksSlotsHistory = getWeeksSlotsHistory(this.weeksSlotsHistory, date, slots)
    return slots
  }

  selectBranchHandler = (_branchId) => {
    // we can't switch branches on UKB in the Appointment Grid,
    // if we could, we would need to set data.branch to a Branch object which
    // has the branchCode attribute! The ID alone is not enough.
  }

  render() {
    const inputSetProps = this.inputSetProps()
    const eventInputSetProps = inputSetProps(['event'])
    const data = this.getHandler()
    const advisor = data.get('advisor')
    const customerType = data.get('customerType')
    const branch = data.get('branch')
    const medium = data.get('medium')
    const value = eventInputSetProps.value
    const phone = computePhone(data)

    return (
      <Application
        title={t(['title'])}
        navigation={<Navigation data={this.getHandler()} next={this.previousHandler} />}>
        <form onSubmit={this.onSubmit}>
          <AppointmentGrid
            weeksSlotsHistory={this.weeksSlotsHistory}
            {...eventInputSetProps}
            selectBranch={this.selectBranchHandler}
            loadSlots={this.loadSlots}
            untilDays={14}
            showNoEvents={false}
            phone={phone}
            medium={medium}
            renderHeader={(props) => {
              return <AppointmentGridHeader {...props} advisor={advisor} branch={branch} customerType={customerType} />
            }}
            renderFooter={(props) => {
              return <AppointmentGridFooter {...props} branch={branch} customerType={customerType} />
            }}
          />

          <Error
            errorMessage={
              validationHasError(eventInputSetProps.errorMessage && eventInputSetProps.errorMessage.toJS()) &&
              i18n.trans(['attributes', 'errors', 'event'])
            }
          />

          {value && (
            <AppointmentSummary
              advisor={advisor}
              branch={branch}
              customerType={customerType}
              medium={medium}
              value={value}
            />
          )}

          <br />
          <NavigationButtons
            nextAction={value && this.nextHandler}
            previousAction={this.previousHandler}
            order={['previous', 'next']}
          />
        </form>
      </Application>
    )
  }
}

export default Appointment
